import { useCallback, useState } from 'react';
import axios from 'axios';
import type { DesignDocument } from '@mcp-artwork/cimdoc';
import { isDesignDocumentWithPanels, useCancelableEffect } from '../../../tools';
import type { PanelProperties, PreviewData, PreviewDataLoader, PreviewType } from './types';

interface UseLoadCardData<T extends PreviewType> {
    data: PreviewData<T> | undefined;
    isLoading: boolean;
    loadingLabel: string;
    hasError: boolean;
    errorMessage: string;
    panels: PanelProperties[];
    panelIndex: number;
    onPreviewLoading: () => void;
    onPreviewLoaded: () => void;
    onPreviewError: () => void;
    onPanelIndexChange: (index: number) => void;
}

async function getPanelsProperties<T extends PreviewType>(
    data: PreviewData<T>,
    signal: AbortSignal,
): Promise<{ panels: PanelProperties[]; agsMetadata?: Record<string, any> }> {
    if ('imageUrl' in data || 'childComponent' in data) {
        return { panels: [] };
    }

    let document: DesignDocument;
    let agsMetadata: Record<string, any> | undefined;

    if ('document' in data) {
        document = data.document.document;
    } else if ('documentUrl' in data) {
        if (!data.documentUrl) {
            return { panels: [] };
        }

        const response = await axios.get(data.documentUrl, { signal });
        const cimDoc = response.data;
        document = cimDoc.document;

        try {
            const metadataHeader = response.headers['x-ags-metadata'];
            if (metadataHeader) {
                agsMetadata = JSON.parse(metadataHeader);
            }
        } catch (e) {
            console.error('Failed to parse x-ags-metadata header', e);
        }
    } else {
        return { panels: [] };
    }

    const panelProperties: PanelProperties[] =
        (isDesignDocumentWithPanels(document) ? document.panels : document.surfaces)?.map((panel) => ({
            id: panel.id,
            width: panel.width,
            height: panel.height,
            images: panel.images,
            textAreas: panel.textAreas,
            itemReferences: panel.itemReferences,
            shapes: panel.shapes,
        })) ?? [];

    return {
        panels: panelProperties.filter((p) => p.images || p.textAreas || p.itemReferences || p.shapes),
        agsMetadata,
    };
}

function getErrorMessage(error: any): string {
    if (error?.response?.data?.message) {
        return error.response.data.message;
    } else if (error?.response?.data?.error) {
        return error.response.data.error;
    } else if (error?.response) {
        switch (error.response.status) {
            case 400:
                return 'Bad request, please verify input is correct';
            case 414:
                return 'URL is too long.';
            case 422:
                return 'Cannot process given document, please verify input is correct.';
            default:
                return 'Unknown error';
        }
    } else if (error?.message) {
        return error.message;
    } else {
        return 'Unknown error';
    }
}

export function useLoadCardData<T extends PreviewType>(
    loader: PreviewDataLoader<T>,
    onAgsMetadataLoaded?: (agsMetadata: Record<string, any>) => void,
): UseLoadCardData<T> {
    const [data, setData] = useState<PreviewData<T> | undefined>(undefined);

    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');

    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [panels, setPanels] = useState<PanelProperties[]>([]);
    const [panelIndex, setPanelIndex] = useState(0);

    useCancelableEffect(
        (signal) => {
            setData(undefined);
            setIsLoading(true);
            setHasError(false);
            setErrorMessage('');
            setPanels([]);
            setPanelIndex(0);
            if (onAgsMetadataLoaded) {
                onAgsMetadataLoaded({});
            }

            function handleError(error: any): void {
                if (signal.aborted) {
                    return;
                }

                setIsLoading(false);
                setHasError(true);
                setErrorMessage(getErrorMessage(error));
            }

            loader({ onStatusUpdate: setLoadingLabel, signal })
                .then((data) => {
                    if ('childComponent' in data) {
                        setData(data);
                        setIsLoading(false);
                    } else {
                        getPanelsProperties(data, signal)
                            .then(({ panels, agsMetadata }) => {
                                setPanels(panels);
                                setData(data);
                                setIsLoading(false);
                                if (agsMetadata && onAgsMetadataLoaded) {
                                    onAgsMetadataLoaded(agsMetadata);
                                }
                            })
                            .catch(handleError);
                    }
                })
                .catch(handleError);
        },
        [loader, onAgsMetadataLoaded],
    );

    const onPreviewLoading = useCallback(() => {
        setIsLoading(true);
        setHasError(false);
        setErrorMessage('');
    }, []);

    const onPreviewLoaded = useCallback(() => {
        setIsLoading(false);
    }, []);

    return {
        data,
        isLoading,
        loadingLabel,
        hasError,
        errorMessage,
        panels,
        panelIndex,
        onPreviewLoading,
        onPreviewLoaded,
        onPreviewError: () => {
            setIsLoading(false);
            setHasError(true);
            setErrorMessage(data?.previewErrorMessage ?? 'Rendering: Unknown error');
        },
        onPanelIndexChange: (index: number) => {
            setPanelIndex(index);
        },
    };
}
